/**
 * Stripe's customer tax ID types (BI13): the same set Checkout collects. Stripe validates the value
 * and its sentence is shown verbatim; this list only drives the picker.
 */
export interface TaxIdType {
  code: string
  country: string
  label: string
}

const T = (code: string, country: string, label: string): TaxIdType => ({ code, country, label })

export const TAX_ID_TYPES: readonly TaxIdType[] = [
  T('eu_vat', 'European Union', 'VAT number'),
  T('eu_oss_vat', 'European Union', 'OSS VAT number'),
  T('gb_vat', 'United Kingdom', 'VAT number'),
  T('us_ein', 'United States', 'EIN'),
  T('ca_bn', 'Canada', 'Business number (BN)'),
  T('ca_gst_hst', 'Canada', 'GST/HST number'),
  T('ca_pst_bc', 'Canada', 'PST number (British Columbia)'),
  T('ca_pst_mb', 'Canada', 'PST number (Manitoba)'),
  T('ca_pst_sk', 'Canada', 'PST number (Saskatchewan)'),
  T('ca_qst', 'Canada', 'QST number (Quebec)'),
  T('au_abn', 'Australia', 'ABN'),
  T('au_arn', 'Australia', 'ARN'),
  T('nz_gst', 'New Zealand', 'GST number'),
  T('ch_vat', 'Switzerland', 'VAT number'),
  T('ch_uid', 'Switzerland', 'UID number'),
  T('li_vat', 'Liechtenstein', 'VAT number'),
  T('li_uid', 'Liechtenstein', 'UID number'),
  T('no_vat', 'Norway', 'VAT number'),
  T('no_voec', 'Norway', 'VOEC number'),
  T('is_vat', 'Iceland', 'VAT number'),
  T('es_cif', 'Spain', 'CIF'),
  T('de_stn', 'Germany', 'Steuernummer'),
  T('hr_oib', 'Croatia', 'OIB'),
  T('hu_tin', 'Hungary', 'Tax number'),
  T('bg_uic', 'Bulgaria', 'UIC'),
  T('ro_tin', 'Romania', 'Tax ID'),
  T('si_tin', 'Slovenia', 'Tax ID'),
  T('rs_pib', 'Serbia', 'PIB'),
  T('me_pib', 'Montenegro', 'PIB'),
  T('mk_vat', 'North Macedonia', 'VAT number'),
  T('al_tin', 'Albania', 'Tax ID'),
  T('ba_tin', 'Bosnia and Herzegovina', 'Tax ID'),
  T('md_vat', 'Moldova', 'VAT number'),
  T('ua_vat', 'Ukraine', 'VAT number'),
  T('by_tin', 'Belarus', 'Tax ID'),
  T('ru_inn', 'Russia', 'INN'),
  T('ru_kpp', 'Russia', 'KPP'),
  T('tr_tin', 'Turkey', 'Tax ID'),
  T('ge_vat', 'Georgia', 'VAT number'),
  T('am_tin', 'Armenia', 'Tax ID'),
  T('kz_bin', 'Kazakhstan', 'BIN'),
  T('kg_tin', 'Kyrgyzstan', 'Tax ID'),
  T('tj_tin', 'Tajikistan', 'Tax ID'),
  T('uz_tin', 'Uzbekistan', 'Tax ID'),
  T('uz_vat', 'Uzbekistan', 'VAT number'),
  T('il_vat', 'Israel', 'VAT number'),
  T('ae_trn', 'United Arab Emirates', 'TRN'),
  T('sa_vat', 'Saudi Arabia', 'VAT number'),
  T('bh_vat', 'Bahrain', 'VAT number'),
  T('om_vat', 'Oman', 'VAT number'),
  T('eg_tin', 'Egypt', 'Tax ID'),
  T('ma_vat', 'Morocco', 'VAT number'),
  T('za_vat', 'South Africa', 'VAT number'),
  T('ng_tin', 'Nigeria', 'Tax ID'),
  T('ke_pin', 'Kenya', 'PIN'),
  T('tz_vat', 'Tanzania', 'VAT number'),
  T('ug_tin', 'Uganda', 'Tax ID'),
  T('zm_tin', 'Zambia', 'Tax ID'),
  T('zw_tin', 'Zimbabwe', 'Tax ID'),
  T('sn_ninea', 'Senegal', 'NINEA'),
  T('gn_nif', 'Guinea', 'NIF'),
  T('mr_nif', 'Mauritania', 'NIF'),
  T('cd_nif', 'DR Congo', 'NIF'),
  T('ao_tin', 'Angola', 'Tax ID'),
  T('in_gst', 'India', 'GST number'),
  T('np_pan', 'Nepal', 'PAN'),
  T('jp_cn', 'Japan', 'Corporate number'),
  T('jp_rn', 'Japan', 'Registered foreign businesses number'),
  T('jp_trn', 'Japan', 'Tax registration number'),
  T('kr_brn', 'South Korea', 'Business registration number'),
  T('cn_tin', 'China', 'Tax ID'),
  T('tw_vat', 'Taiwan', 'VAT number'),
  T('hk_br', 'Hong Kong', 'Business registration number'),
  T('sg_gst', 'Singapore', 'GST number'),
  T('sg_uen', 'Singapore', 'UEN'),
  T('my_frp', 'Malaysia', 'FRP number'),
  T('my_itn', 'Malaysia', 'ITN'),
  T('my_sst', 'Malaysia', 'SST number'),
  T('th_vat', 'Thailand', 'VAT number'),
  T('vn_tin', 'Vietnam', 'Tax ID'),
  T('kh_tin', 'Cambodia', 'Tax ID'),
  T('id_npwp', 'Indonesia', 'NPWP'),
  T('ph_tin', 'Philippines', 'Tax ID'),
  T('br_cnpj', 'Brazil', 'CNPJ'),
  T('br_cpf', 'Brazil', 'CPF'),
  T('ar_cuit', 'Argentina', 'CUIT'),
  T('cl_tin', 'Chile', 'Tax ID'),
  T('co_nit', 'Colombia', 'NIT'),
  T('pe_ruc', 'Peru', 'RUC'),
  T('ec_ruc', 'Ecuador', 'RUC'),
  T('uy_ruc', 'Uruguay', 'RUC'),
  T('bo_tin', 'Bolivia', 'Tax ID'),
  T('ve_rif', 'Venezuela', 'RIF'),
  T('mx_rfc', 'Mexico', 'RFC'),
  T('cr_tin', 'Costa Rica', 'Tax ID'),
  T('sv_nit', 'El Salvador', 'NIT'),
  T('do_rcn', 'Dominican Republic', 'RCN'),
  T('bs_tin', 'Bahamas', 'Tax ID'),
  T('bb_tin', 'Barbados', 'Tax ID'),
  T('sr_fin', 'Suriname', 'FIN'),
  T('ad_nrt', 'Andorra', 'NRT'),
]

export function taxIdLabel(code: string): string {
  const found = TAX_ID_TYPES.find((type) => type.code === code)
  return found === undefined ? code : `${found.country} ${found.label}`
}

/** The picker's countries, in alphabetical order, and the types each offers. */
export function taxIdCountries(): string[] {
  return [...new Set(TAX_ID_TYPES.map((type) => type.country))].sort((a, b) => a.localeCompare(b))
}

export function taxIdTypesFor(country: string): TaxIdType[] {
  return TAX_ID_TYPES.filter((type) => type.country === country)
}

export function taxIdProblem(type: string, value: string): string | null {
  if (type === '') return 'Choose the type of tax ID.'
  if (value.trim() === '') return 'Enter the tax ID.'
  return null
}
